import { Injectable, inject } from '@angular/core';
import {
  Entity,
  EntityApplications,
  EntityPermissionActivityKeys,
  UserRole,
  UserRoleListItemResponse,
  UserRoleModelKeys,
} from '@ag-common-lib/public-api';
import { Observable, from, map, shareReplay } from 'rxjs';
import { QueryParam } from '../dao/CommonFireStoreDao.dao';
import { IdentityApiService } from './identity-api.service';
import { UserRolesCatalogService } from './user-roles-catalog.service';

/**
 * Roles now live in SQL behind /api/identity, not in the Firestore `user-roles`
 * collection.
 *
 * This keeps the shape its callers already use — `getList(queryParams)` returning an
 * observable of `UserRole` — so the eight call sites that only ever read roles did
 * not have to change. What changed underneath is significant: reads are one shared,
 * cached HTTP call refreshed by a change signal, where before every caller (including
 * two PURE PIPES used inside *ngFor cells) opened its own full-collection Firestore
 * listener.
 */
@Injectable({ providedIn: 'root' })
export class UserRolesService {
  private readonly identityApi = inject(IdentityApiService);
  private readonly catalog = inject(UserRolesCatalogService);

  /**
   * Only the two filters the callers actually pass are honoured — `isEnable` and
   * `appId`. Anything else is ignored rather than silently mistranslated into a
   * different query; the API is the place to add a real filter if one is needed.
   */
  getList(queryParams?: QueryParam[], _options?: unknown): Observable<UserRole[]> {
    const appId = this.readParam(queryParams, UserRoleModelKeys.appId) as EntityApplications | undefined;
    const isEnable = this.readParam(queryParams, UserRoleModelKeys.isEnable);
    const includeDisabled = isEnable === true ? false : true;

    return this.catalog
      .getRoles(appId, includeDisabled)
      .pipe(map(roles => roles.map(role => UserRolesService.toUserRole(role))));
  }

  getRoleById(externalId: string): Observable<UserRole> {
    return from(this.identityApi.getRole(externalId)).pipe(
      map(role => UserRolesService.toUserRole(role as UserRoleListItemResponse)),
      shareReplay(1),
    );
  }

  create(role: Partial<UserRole>): Promise<UserRole> {
    return this.identityApi
      .createRole({
        role: role?.[UserRoleModelKeys.role] as string,
        appId: role?.[UserRoleModelKeys.appId] as EntityApplications,
        activity: role?.[UserRoleModelKeys.activity],
        isEnable: role?.[UserRoleModelKeys.isEnable],
        assignmentTrigger: role?.[UserRoleModelKeys.assignmentTrigger],
      })
      .then(created => UserRolesService.toUserRole(created as UserRoleListItemResponse));
  }

  /**
   * `activity` merges server-side, preserving what the Firestore merge-write did:
   * the editor saves one toggled entity at a time and must not wipe the rest.
   */
  updateFields(externalId: string, data: Partial<UserRole>): Promise<UserRole> {
    return this.identityApi
      .updateRole(externalId, {
        role: data?.[UserRoleModelKeys.role],
        activity: data?.[UserRoleModelKeys.activity] as Record<string, Record<string, boolean>> | undefined,
        isEnable: data?.[UserRoleModelKeys.isEnable],
        assignmentTrigger: data?.[UserRoleModelKeys.assignmentTrigger],
      })
      .then(updated => UserRolesService.toUserRole(updated as UserRoleListItemResponse));
  }

  /** Disable; the row survives because other documents store its id. */
  disable(externalId: string): Promise<UserRole> {
    return this.identityApi
      .disableRole(externalId)
      .then(updated => UserRolesService.toUserRole(updated as UserRoleListItemResponse));
  }

  /**
   * Which roles grant an activity on an entity — the conference and form-management
   * ACL pickers. Resolved by the API against a GIN index instead of by downloading
   * every role and filtering in the browser.
   */
  getRoleIdsByEntity(
    entity: Entity,
    appId: EntityApplications,
    entityPermissionActivity: EntityPermissionActivityKeys = EntityPermissionActivityKeys.write,
  ): Observable<string[]> {
    return this.catalog.getRoleIdsByEntity(entity, appId, entityPermissionActivity);
  }

  /**
   * `dbId` is mapped from `externalId`, which IS the old Firestore document id — so
   * role-id arrays already stored on agent, conference and form documents keep
   * matching.
   */
  static toUserRole(role: UserRoleListItemResponse): UserRole {
    return {
      dbId: role.externalId,
      [UserRoleModelKeys.role]: role.role,
      [UserRoleModelKeys.appId]: role.appId,
      [UserRoleModelKeys.activity]: role.activity,
      [UserRoleModelKeys.isEnable]: role.isEnable,
      [UserRoleModelKeys.assignmentTrigger]: role.assignmentTrigger,
    } as unknown as UserRole;
  }

  private readParam(queryParams: QueryParam[] | undefined, field: string): unknown {
    return queryParams?.find(param => param.field === field)?.value;
  }
}
